import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  addContactApi,
  createSegmentApi,
  deleteContactApi,
  deleteSegmentApi,
  getContactStatsApi,
  importContactsApi,
  listContactsApi,
  listSegmentsApi,
  updateContactApi,
} from '../../api/contactsClient';
import {
  buildListContactsPayload,
  isApiContactRecord,
  mapApiContactToUi,
  mapApiContactStatsToUi,
  mapApiSegmentToUi,
  mapCreatedContactToUi,
} from '../../pages/whatsapp/utils/contactsApiMapper';
import { CONTACTS_PAGE_SIZE, CONTACT_STATS_API_ENABLED } from '../../pages/whatsapp/data/contactsData';
import { buildImportContactsMappingPayload } from '../../pages/whatsapp/utils/contactsImportUtils';
import { parseS3FileUrl } from '../../utils/s3MediaUrl';
import { uploadFileToS3 } from './fileUploadHelper';
import { showAlert } from '../alertSlice';
import { hideLoader, showLoader } from '../loaderSlice';
import { callWithAuth } from '../sagaApiHelper';
import {
  addContactRequest,
  addContactFailure,
  addContactStart,
  addContactSuccess,
  createSegmentFinish,
  createSegmentRequest,
  createSegmentStart,
  deleteContactRequest,
  deleteContactFinish,
  deleteContactStart,
  deleteSegmentRequest,
  deleteSegmentFinish,
  deleteSegmentStart,
  fetchContactsFailure,
  fetchContactsRequest,
  fetchContactsStart,
  fetchContactsSuccess,
  fetchContactStatsFailure,
  fetchContactStatsRequest,
  fetchContactStatsStart,
  fetchContactStatsSuccess,
  fetchSegmentsFailure,
  fetchSegmentsRequest,
  fetchSegmentsStart,
  fetchSegmentsSuccess,
  importContactsFailure,
  importContactsRequest,
  importContactsStart,
  importContactsSuccess,
  removeContact,
  removeSegment,
  updateContactRequest,
  upsertContact,
} from '../slices/contactsSlice';
import { ensureWhatsAppModuleAccess } from './helpers/whatsAppAccessHelper';

function* handleFetchContacts(action) {
  if (!(yield call(ensureWhatsAppModuleAccess))) return;

  const listParams = action.payload || { search: '', limit: 10 };

  try {
    yield put(fetchContactsStart(listParams));

    const req = {
      operation: 'LIST_CONTACTS',
      payload: buildListContactsPayload(listParams),
    };
    const res = yield call(callWithAuth, listContactsApi, req);

    if (res?.status) {
      const contacts = (res.data?.contacts || [])
        .filter(isApiContactRecord)
        .map(mapApiContactToUi);
      yield put(
        fetchContactsSuccess({
          contacts,
          lastEvaluatedKey: res.data?.lastEvaluatedKey ?? null,
        })
      );
      return;
    }

    yield put(fetchContactsFailure(res?.errMsg || 'Failed to load contacts'));
  } catch (error) {
    yield put(fetchContactsFailure(error.message || 'Failed to load contacts'));
  }
}

function* refetchContactStats() {
  if (!CONTACT_STATS_API_ENABLED) return;
  yield put(fetchContactStatsRequest());
}

function* handleFetchContactStats() {
  if (!CONTACT_STATS_API_ENABLED) return;
  if (!(yield call(ensureWhatsAppModuleAccess))) return;

  try {
    yield put(fetchContactStatsStart());

    const req = { operation: 'GET_CONTACT_STATS', payload: {} };
    const res = yield call(callWithAuth, getContactStatsApi, req);

    if (res?.status) {
      yield put(fetchContactStatsSuccess(mapApiContactStatsToUi(res.data)));
      return;
    }

    yield put(fetchContactStatsFailure(res?.errMsg || 'Failed to load contact stats'));
  } catch (error) {
    yield put(fetchContactStatsFailure(error.message || 'Failed to load contact stats'));
  }
}

function listParamsWithoutCursor(params = {}) {
  const { lastEvaluatedKey, search, ...rest } = params;
  return rest;
}

function* refetchContactsListFromStart() {
  const { lastListParams } = yield select((state) => state.contacts);
  yield put(
    fetchContactsRequest({
      ...listParamsWithoutCursor(lastListParams),
      search: '',
      limit: CONTACTS_PAGE_SIZE,
    })
  );
}

function* refetchContacts() {
  const { lastListParams } = yield select((state) => state.contacts);
  yield put(
    fetchContactsRequest({
      search: lastListParams.search || '',
      limit: lastListParams.limit,
      leadStatus: lastListParams.leadStatus,
      tags: lastListParams.tags,
    })
  );
}

function* handleAddContact(action) {
  if (!(yield call(ensureWhatsAppModuleAccess))) return;

  try {
    yield put(addContactStart());
    yield put(showLoader());

    const req = {
      operation: 'ADD_CONTACT',
      payload: action.payload,
    };
    const res = yield call(callWithAuth, addContactApi, req);

    if (res?.status) {
      const customerId = res.data?.customerId;
      if (customerId) {
        yield put(upsertContact(mapCreatedContactToUi(action.payload, customerId)));
      }
      yield put(addContactSuccess());
      yield put(
        showAlert({
          message: res.data?.message || 'Contact added successfully.',
          type: 'success',
        })
      );
      yield call(refetchContactsListFromStart);
      yield call(refetchContactStats);
      return;
    }

    yield put(addContactFailure());
    yield put(
      showAlert({
        message: res?.errMsg || 'Failed to add contact',
        type: 'error',
      })
    );
  } catch (error) {
    yield put(addContactFailure());
    yield put(
      showAlert({
        message: error.message || 'Failed to add contact',
        type: 'error',
      })
    );
  } finally {
    yield put(hideLoader());
  }
}

function* handleUpdateContact(action) {
  if (!(yield call(ensureWhatsAppModuleAccess))) return;

  try {
    yield put(showLoader());

    const req = {
      operation: 'UPDATE_CONTACT',
      payload: action.payload,
    };
    const res = yield call(callWithAuth, updateContactApi, req);

    if (res?.status) {
      const apiContact = res.data?.contact;
      if (apiContact) {
        yield put(upsertContact(mapApiContactToUi(apiContact)));
      } else {
        yield call(refetchContacts);
      }
      yield put(
        showAlert({
          message: res.data?.message || 'Contact updated successfully.',
          type: 'success',
        })
      );
      return;
    }

    yield put(
      showAlert({
        message: res?.errMsg || 'Failed to update contact',
        type: 'error',
      })
    );
  } catch (error) {
    yield put(
      showAlert({
        message: error.message || 'Failed to update contact',
        type: 'error',
      })
    );
  } finally {
    yield put(hideLoader());
  }
}

function* handleDeleteContact(action) {
  if (!(yield call(ensureWhatsAppModuleAccess))) return;

  const customerId = action.payload;

  try {
    yield put(deleteContactStart());
    yield put(showLoader());

    const req = {
      operation: 'DELETE_CONTACT',
      payload: { customerId },
    };
    const res = yield call(callWithAuth, deleteContactApi, req);

    if (res?.status) {
      yield put(removeContact(customerId));
      yield put(
        showAlert({
          message: res.data?.message || 'Contact deleted successfully.',
          type: 'success',
        })
      );
      yield call(refetchContactStats);
      return;
    }

    yield put(
      showAlert({
        message: res?.errMsg || 'Failed to delete contact',
        type: 'error',
      })
    );
  } catch (error) {
    yield put(
      showAlert({
        message: error.message || 'Failed to delete contact',
        type: 'error',
      })
    );
  } finally {
    yield put(deleteContactFinish());
    yield put(hideLoader());
  }
}

function* handleFetchSegments() {
  if (!(yield call(ensureWhatsAppModuleAccess))) return;

  try {
    yield put(fetchSegmentsStart());

    const req = { operation: 'LIST_SEGMENTS', payload: {} };
    const res = yield call(callWithAuth, listSegmentsApi, req);

    if (res?.status) {
      const segments = (res.data?.segments || []).map(mapApiSegmentToUi);
      yield put(fetchSegmentsSuccess(segments));
      return;
    }

    yield put(fetchSegmentsFailure(res?.errMsg || 'Failed to load segments'));
  } catch (error) {
    yield put(fetchSegmentsFailure(error.message || 'Failed to load segments'));
  }
}

function* handleCreateSegment(action) {
  if (!(yield call(ensureWhatsAppModuleAccess))) return;

  try {
    yield put(createSegmentStart());

    const req = {
      operation: 'CREATE_SEGMENT',
      payload: action.payload,
    };
    const res = yield call(callWithAuth, createSegmentApi, req);

    if (res?.status) {
      yield put(
        showAlert({
          message: res.data?.message || 'Segment created successfully.',
          type: 'success',
        })
      );
      yield put(fetchSegmentsRequest());
      return;
    }

    yield put(
      showAlert({
        message: res?.errMsg || 'Failed to create segment',
        type: 'error',
      })
    );
  } catch (error) {
    yield put(
      showAlert({
        message: error.message || 'Failed to create segment',
        type: 'error',
      })
    );
  } finally {
    yield put(createSegmentFinish());
  }
}

function* handleDeleteSegment(action) {
  if (!(yield call(ensureWhatsAppModuleAccess))) return;

  const segmentId = action.payload;

  try {
    yield put(deleteSegmentStart());
    yield put(showLoader());

    const req = {
      operation: 'DELETE_SEGMENT',
      payload: { segmentId },
    };
    const res = yield call(callWithAuth, deleteSegmentApi, req);

    if (res?.status) {
      yield put(removeSegment(segmentId));
      yield put(
        showAlert({
          message: res.data?.message || 'Segment deleted successfully.',
          type: 'success',
        })
      );
      return;
    }

    yield put(
      showAlert({
        message: res?.errMsg || 'Failed to delete segment',
        type: 'error',
      })
    );
  } catch (error) {
    yield put(
      showAlert({
        message: error.message || 'Failed to delete segment',
        type: 'error',
      })
    );
  } finally {
    yield put(deleteSegmentFinish());
    yield put(hideLoader());
  }
}

function formatImportContactsResultMessage(data = {}) {
  const base = data.message || 'Contacts imported successfully.';
  const parts = [];
  if (typeof data.createdCount === 'number') {
    parts.push(`${data.createdCount} created`);
  }
  if (typeof data.updatedCount === 'number') {
    parts.push(`${data.updatedCount} updated`);
  }
  if (typeof data.failedCount === 'number') {
    parts.push(`${data.failedCount} failed`);
  }
  return parts.length ? `${base} (${parts.join(', ')})` : base;
}

function* handleImportContacts(action) {
  if (!(yield call(ensureWhatsAppModuleAccess))) return;

  const { file, mapping } = action.payload;

  try {
    yield put(importContactsStart());
    yield put(showLoader());

    const companyId = yield select(
      (state) =>
        state.auth?.user?.companyId ||
        state.companyInfo?.info?.companyId ||
        state.companyInfo?.info?.id ||
        ''
    );

    if (!companyId) {
      yield put(importContactsFailure());
      yield put(
        showAlert({
          message: 'Company ID is missing. Please sign in again and retry.',
          type: 'error',
        })
      );
      return;
    }

    // crm-upload → prodyt-global-media via operation "contacts"
    // key: contacts/{companyId}/{year}/{month}/{day}/{hour}/{fileName}
    // Same uploadFileToS3 pattern as chat / profile / bug-report (operation selects path).
    // Browsers sometimes leave CSV file.type empty — normalize before shared helper.
    const csvFile =
      file.type
        ? file
        : new File([file], file.name, { type: 'text/csv' });
    const s3Key = yield call(uploadFileToS3, csvFile, companyId, 'contacts');
    const { bucket, key } = parseS3FileUrl(s3Key);

    if (!bucket || !key) {
      yield put(importContactsFailure());
      yield put(
        showAlert({
          message: 'Failed to resolve uploaded file location',
          type: 'error',
        })
      );
      return;
    }

    const req = {
      operation: 'IMPORT_CONTACTS',
      payload: {
        bucket,
        key,
        onDuplicate: 'update',
        mapping: buildImportContactsMappingPayload(mapping),
      },
    };
    const res = yield call(callWithAuth, importContactsApi, req);

    if (res?.status) {
      yield put(importContactsSuccess());
      yield put(
        showAlert({
          message: formatImportContactsResultMessage(res.data),
          type: res.data?.failedCount > 0 ? 'warning' : 'success',
        })
      );
      yield call(refetchContactsListFromStart);
      yield call(refetchContactStats);
      return;
    }

    yield put(importContactsFailure());
    yield put(
      showAlert({
        message: res?.errMsg || 'Failed to import contacts',
        type: 'error',
      })
    );
  } catch (error) {
    yield put(importContactsFailure());
    yield put(
      showAlert({
        message: error.message || 'Failed to import contacts',
        type: 'error',
      })
    );
  } finally {
    yield put(hideLoader());
  }
}

export default function* contactsSaga() {
  yield takeLatest(fetchContactsRequest.type, handleFetchContacts);
  yield takeLatest(fetchContactStatsRequest.type, handleFetchContactStats);
  yield takeLatest(addContactRequest.type, handleAddContact);
  yield takeLatest(updateContactRequest.type, handleUpdateContact);
  yield takeLatest(deleteContactRequest.type, handleDeleteContact);
  yield takeLatest(fetchSegmentsRequest.type, handleFetchSegments);
  yield takeLatest(createSegmentRequest.type, handleCreateSegment);
  yield takeLatest(deleteSegmentRequest.type, handleDeleteSegment);
  yield takeLatest(importContactsRequest.type, handleImportContacts);
}
